import _ from 'lodash';
import type { Iterable } from 'immutable';
import moment from 'moment';
import React, { useState } from 'react';
import { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form/immutable';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';

import StepperComponent, { Stepper } from 'retired/Stepper';
import StepLabel from 'retired/Stepper/StepLabel';
import Step from 'retired/Stepper/Step';
import PageHeader from 'retired/PageHeader';
import { scrollToError } from 'utils/scroll.helper';
import Campaign from 'entities/campaign/model';
import { selectCampaigns } from 'entities/campaign/selectors';
import Deed from 'entities/deed/model';
import { selectUser } from 'entities/user/selectors';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import { MinAction } from 'shared/types';

import { getSteps } from './steps';
import { useSetCampaignActions } from './hooks';
import {
  CAMPAIGN_PAGE_FORM,
  saveAction,
  CampaignFormType,
  saveFailedAction,
  type FormData,
} from './types';

const getNewContentSorting = (data: FormData, selectedActions: MinAction[]) => {
  const fundraisersSorting = _.map(data.fundraisers, 'id');
  const volunteeringSorting = _.map(data.volunteering, 'id');
  const eventsSorting = _.map(data.events, 'id');
  const actionsSorting = _.map(selectedActions, 'id');

  return {
    fundraisers: fundraisersSorting,
    volunteering: volunteeringSorting,
    events: eventsSorting,
    actions: actionsSorting,
  };
};

const getAddedAndRemovedDeeds = (
  formData: FormData,
  attachedDeeds: Deed[] | null
): { addedDeeds: string[]; removedDeeds: string[] } => {
  if (!attachedDeeds) {
    return { addedDeeds: [], removedDeeds: [] };
  }

  const originalDeedsIds = _.map(attachedDeeds, 'id');

  const newDeedIds = _.map(
    [
      ...(formData?.fundraisers || []),
      ...(formData?.volunteering || []),
      ...(formData?.events || []),
    ],
    'id'
  );

  const addedDeeds = _.difference(newDeedIds, originalDeedsIds);

  const removedDeeds = _.difference(originalDeedsIds, newDeedIds);

  return { addedDeeds, removedDeeds };
};

const CampaignForm: React.FC<InjectedFormProps<FormData>> = (props) => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const campaignType = queryParams.get('type') as CampaignFormType;
  const duplicate = queryParams.get('duplicate');
  const { id } = useParams<{ id?: string }>();
  const [image, setImage] = useState(!id);
  const { handleSubmit, submitting } = props;
  const user = useSelector(selectUser);
  const showActionsStep = !!(
    user &&
    user.hasFeature(Features.ACTIONS) &&
    user.hasPrivilege(Privileges.ACTIONS_MANAGE)
  );
  const dispatch = useDispatch();
  const campaigns: Iterable<string, Campaign> = useSelector(selectCampaigns);
  const campaign = (id ? campaigns?.get?.(id)?.toJS() : undefined) as Campaign | undefined;
  const history = useHistory();
  const [selectedActions, setSelectedActions] = useState<MinAction[]>([]);
  const isPledge = campaign?.isPledge || [CampaignFormType.GivingPledge].includes(campaignType);
  const actionsSorting = campaign?.contentSorting.items.actions || [];

  const steps = getSteps({
    props,
    image,
    setImage,
    showActionsStep,
    campaignId: id,
    selectedActions,
    setSelectedActions,
    isPledge,
    actionsSorting,
  });

  const loadingCampaign = !!id && !campaign;

  const { onSetCampaignActions } = useSetCampaignActions();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submit = (formData: any) => {
    const data = formData?.toJS() as FormData;
    const deeds = campaign?.deeds || [];

    const { addedDeeds, removedDeeds } = getAddedAndRemovedDeeds(data, deeds);

    const newContentSorting = getNewContentSorting(data, selectedActions);

    const contentSorting = {
      sections: data.contentSortingSections,
      items: newContentSorting,
    };

    const { pledgedDonationStartingAt, startingAt } = data;

    if (pledgedDonationStartingAt && startingAt) {
      if (moment(pledgedDonationStartingAt).isBefore(startingAt)) {
        dispatch(
          saveFailedAction(
            'The Campaign\'s "Deduction Start Date" cannot be before the Campaign\'s "Start Date"',
            ''
          )
        );

        return;
      }
    }

    dispatch(
      saveAction({
        id,
        ..._.omit(data, ['company', 'privacy', 'isSharedWithSubsidiary', 'optedIn']),
        ...(data.privacy === 'shared'
          ? {
              public: false,
              companySubsidiaryAccess: true,
            }
          : data.privacy === 'public'
          ? { public: true, companySubsidiaryAccess: false }
          : { public: false, companySubsidiaryAccess: false }),
        backgroundImage: image ? data.backgroundImage : '',
        backgroundHeroColor: !image ? data.backgroundHeroColor : '',
        nonprofits: (data.nonprofits ?? []).map((nonprofit) => {
          const nonprofitForProcessing = {
            id: nonprofit.id,
            externalId: nonprofit.externalId,
            nonprofitId: nonprofit.nonprofitId,
          };

          return nonprofitForProcessing;
        }),
        contentSorting,
        addedDeeds,
        removedDeeds,
        setCampaignActions: (campaignId: string) => {
          if (!showActionsStep) {
            return;
          }

          void onSetCampaignActions({
            campaignId,
            actionIds: selectedActions.map((action) => action.id),
          });
        },
        isPledge,
      })
    );
  };

  const getCampaignFormHeaderTitle = () => {
    if (campaign) {
      const { title } = campaign;
      return `${duplicate ? 'Duplicate' : 'Edit'} "${title}" campaign`;
    }

    if (campaignType) {
      if (campaignType === CampaignFormType.Campaign) {
        return `Create campaign`;
      }

      return `Create ${campaignType} campaign`;
    }

    return 'Create campaign';
  };

  return (
    <form onSubmit={handleSubmit(submit)} style={{ maxWidth: 1200 }}>
      <PageHeader
        title={getCampaignFormHeaderTitle()}
        onArrowClick={(): void => {
          history.push('/campaigns');
        }}
        buttonText={id ? 'Save Changes' : 'Submit'}
        buttonType="submit"
        buttonDisabled={submitting || loadingCampaign}
      />

      {loadingCampaign && <LinearProgress />}

      <Stepper orientation="vertical">
        {steps.map((item, index: number) => (
          <StepperComponent key={item.id} labelText={item.label} componentIndex={index}>
            {item.jsx}
          </StepperComponent>
        ))}

        <Step active completed={false}>
          <StepLabel icon={steps.length + 1}>
            <span
              style={{
                fontSize: 20,
                textTransform: 'capitalize',
                fontWeight: 'bold',
              }}
            >
              All set!
            </span>
          </StepLabel>
        </Step>

        <PageHeader
          onArrowClick={(): void => {
            history.push('/campaigns');
          }}
          buttonText={id ? 'Save Changes' : 'Submit'}
          buttonType="submit"
          buttonDisabled={submitting}
          id="step-submit"
        />
      </Stepper>
    </form>
  );
};

const withForm = reduxForm<FormData>({
  form: CAMPAIGN_PAGE_FORM,
  onSubmitFail: scrollToError,
});

export default withForm(CampaignForm);
